import type { FulfillmentOrderDetail, FulfillmentOrderLine } from '@/infrastructure/FulfillmentAdminClient';
import { kstClock } from '@/utils/fulfillmentTime';

/**
 * Arrival Sort (SSD-7326): the door desk scans every arrived JIT parcel before inbound and is told where
 * it goes — straight to packing, into a rebin set, or aside. The rules are DY's Arrived_Tracking_Sort
 * sheet and his 10/8 flow (hold → single → multi → wait), judged per order rather than per parcel, so a
 * parcel is only sent to a rebin set when the rest of its order is here or on its way today.
 *
 * Everything in this file is pure: the page looks the parcel up, this decides, and the numbered rules the
 * page prints under the screen are the same list the verdicts cite.
 */

/** The numbered rules printed under the page; a verdict names the rule that decided it by its number. */
export const ARRIVAL_SORT_RULES: readonly string[] = [
  'First tell what the scanned number is. An inbound parcel of an ordered product is sorted by the rules below. An inbound request parcel, a return parcel, a product barcode or an unknown number is not sorted; the screen points to the page that handles it.',
  "Follow every order that shares the parcel, and every order sharing those orders' other parcels, to the end, and treat them as one bundle that is never split. If the bundle cannot be read in full (too many links or a failed lookup), the parcel is not sorted and goes to Check.",
  'If every order of the bundle is cancelled, or every item in the scanned parcel is cancelled: Cancelled. If only some are cancelled, judge the rest without them and mark the verdict "Includes cancelled".',
  "If the bundle includes an order already outbounded or closed, the scanned parcel's items are already inbounded, or the parcel is not linked to any line: Check.",
  'Hold when an order is on hold, an item not yet here has no parcel number (not bought or not registered), a stock-pick item has no stock reserved, the carrier reported a return, loss or failed delivery, or the carrier delivered an item before today and it has not been scanned.',
  'For each remaining item, look for evidence that it joins today: ① already inbounded ② the parcel just scanned ③ scanned earlier today on this screen ④ delivered by the carrier ⑤ out for delivery ⑥ in transit ⑦ carrier status unknown.',
  'If every remaining item is ① to ④, the order is complete now. If the parcel just scanned is its only outside parcel and nothing is picked from stock: Single (straight to packing, no rebin). Otherwise: Multi.',
  'If only ⑤ out-for-delivery items remain, expect them today and send the parcel to Multi. An out-for-delivery parcel can still miss the day, so the verdict lists it.',
  'If any item is ⑥ in transit or ⑦ unknown: Wait. A Wait verdict also names the Multi group the bundle goes to once complete.',
  'Multi 1 is for bundles with no stock-pick items; Multi 2 and 3 are for bundles with stock-pick items. Between 2 and 3, send the parcel to the one with fewer parcels today (on a tie, fewer units). An order keeps the group it was first given.',
  'Every scan looks for parcels of the same bundle set aside today in Wait or Hold. If this verdict is Single or Multi, the screen says to take them out of Wait and pack them together; if it is Wait, to put this parcel beside them.',
  'Mark Urgent when the order was placed 3 or more days ago (KST).',
  'Scanning the same parcel again today shows when it was first scanned and judges it again on current data.',
  "This screen only reads. It never changes inbound, location or status. Today's scans are kept in this browser only and start over at midnight KST. Scans split across PCs are not combined, so ③ and the take-out prompts go missing; sort on one PC.",
  'Carrier status (④ ⑤ ⑥) is used only when the order detail carries the domestic tracking result; otherwise it is ⑦ unknown. Delivered counts as ④ only when the delivery date is today (KST); without a date it is ⑦ unknown.',
  "For speed, the screen loads the open (awaiting inbound) orders of the last 14 days when it opens and reloads them every 10 minutes. A parcel of those orders is judged on the spot, card and sound together, and then checked again with the server; if the server disagrees, the card changes to the server's verdict, marked Changed, with a warning sound. Orders that leave the list are dropped from memory. A parcel that was not loaded is looked up on the server.",
];

/** Rule numbers as the verdicts cite them; one name per rule so a renumbering is a compile-time edit. */
export const RULE = {
  KIND: 1,
  BUNDLE: 2,
  CANCELLED: 3,
  CHECK: 4,
  HOLD: 5,
  EVIDENCE: 6,
  COMPLETE: 7,
  OUT_FOR_DELIVERY: 8,
  WAIT: 9,
  GROUP: 10,
  RELOCATE: 11,
  URGENT: 12,
  RESCAN: 13,
  READ_ONLY: 14,
  CARRIER: 15,
  SPEED: 16,
} as const;

export type SortClass = 'SINGLE' | 'MULTI_1' | 'MULTI_2' | 'MULTI_3' | 'WAIT' | 'HOLD' | 'CANCELLED' | 'CHECK';
export type MultiGroup = 'MULTI_1' | 'MULTI_2' | 'MULTI_3';

export const SORT_CLASS_LABEL: Readonly<Record<SortClass, string>> = {
  SINGLE: 'Single',
  MULTI_1: 'Multi 1',
  MULTI_2: 'Multi 2',
  MULTI_3: 'Multi 3',
  WAIT: 'Wait',
  HOLD: 'Hold',
  CANCELLED: 'Cancelled',
  CHECK: 'Check',
};

/** Where a parcel in each class physically goes; the line the desk reads first. */
export const SORT_CLASS_ACTION: Readonly<Record<SortClass, string>> = {
  SINGLE: 'Straight to packing, no rebin',
  MULTI_1: 'Rebin set · Multi 1',
  MULTI_2: 'Rebin set · Multi 2',
  MULTI_3: 'Rebin set · Multi 3',
  WAIT: 'To the waiting shelf',
  HOLD: 'Hold: find the cause first',
  CANCELLED: 'Do not pack: to the cancelled area',
  CHECK: 'Not sorted: check the reasons below',
};

/** What each of the eight classes means and the rules that put a parcel there, printed under the screen. */
export const SORT_CLASS_DEFINITION: Readonly<Record<SortClass, { meaning: string; rules: readonly number[] }>> = {
  SINGLE: {
    meaning:
      'Every item of the bundle is in the parcel just scanned and nothing is picked from stock. There is no other box to wait for, so it goes straight to packing without a rebin. If the items of two or more orders came in one parcel, open the box at the packing desk and pack each order separately.',
    rules: [7],
  },
  MULTI_1: {
    meaning:
      'Every other item is already inbounded, here today or out for delivery, and nothing is picked from stock. Pack the bundle together in rebin set Multi 1.',
    rules: [7, 8, 10],
  },
  MULTI_2: {
    meaning:
      'Same as Multi 1, but the bundle has stock-pick items. It goes to Multi 2 when Multi 2 has taken no more parcels today than Multi 3.',
    rules: [7, 8, 10],
  },
  MULTI_3: {
    meaning:
      'A bundle with stock-pick items goes to Multi 3 when Multi 3 has taken fewer parcels today. An order keeps the group it was first given.',
    rules: [7, 8, 10],
  },
  WAIT: {
    meaning:
      'Some item is in transit or its carrier status is unknown, so there is no evidence it arrives today. Put the parcel on the waiting shelf; scanning the last parcel of the order brings up "Take these out of Wait and pack them together".',
    rules: [9, 11],
  },
  HOLD: {
    meaning:
      'An order is on hold, or an item has no parcel number (not bought or not registered), a stock-pick item has no stock reserved, the carrier reported a problem (return, loss, failed delivery), or an item was delivered on an earlier day and never scanned. Find the cause before handling it.',
    rules: [5],
  },
  CANCELLED: {
    meaning:
      'Every order of the bundle is cancelled, or every item in this parcel is cancelled. Do not pack it; set it aside in the cancelled area.',
    rules: [3],
  },
  CHECK: {
    meaning:
      "The bundle includes an order already outbounded or closed, this parcel's items are already inbounded, the parcel matches no line, or the bundle could not be read in full. The screen does not sort it; open the order and check.",
    rules: [2, 4],
  },
};

/** How one remaining line stands against "can this order be packed today" (rule 6). */
export type ParcelState =
  | 'IN'
  | 'HERE'
  | 'SCANNED_TODAY'
  | 'DELIVERED'
  | 'OUT_FOR_DELIVERY'
  | 'IN_TRANSIT'
  | 'UNKNOWN'
  | 'PROBLEM'
  | 'NO_TRACKING'
  | 'NO_STOCK'
  | 'DELIVERED_EARLIER'
  | 'CANCELLED';

export const PARCEL_STATE_LABEL: Readonly<Record<ParcelState, string>> = {
  IN: '① Already inbounded',
  HERE: '② This parcel',
  SCANNED_TODAY: '③ Scanned earlier today',
  DELIVERED: '④ Delivered by carrier',
  OUT_FOR_DELIVERY: '⑤ Out for delivery',
  IN_TRANSIT: '⑥ In transit',
  UNKNOWN: '⑦ Carrier status unknown',
  PROBLEM: 'Carrier reported a problem',
  NO_TRACKING: 'No parcel number',
  NO_STOCK: 'No stock reserved',
  DELIVERED_EARLIER: 'Delivered earlier · not scanned yet',
  CANCELLED: 'Cancelled item',
};

/** States that count as "here, or as good as here" for completing an order today (rule 7). */
const ARRIVED_STATES: ReadonlySet<ParcelState> = new Set<ParcelState>(['IN', 'HERE', 'SCANNED_TODAY', 'DELIVERED']);
/** States that hold the whole bundle (rule 5). */
const HOLD_STATES: ReadonlySet<ParcelState> = new Set<ParcelState>([
  'NO_TRACKING',
  'NO_STOCK',
  'PROBLEM',
  'DELIVERED_EARLIER',
]);

/** Line states whose goods are already in the building or gone; mirrors ARRIVED_LINE_STATUSES. */
const IN_LINE_STATUSES = new Set(['INBOUNDED', 'READY', 'OUTBOUNDED', 'CLOSED']);
const DROPPED_LINE_STATUSES = new Set(['CANCELLED', 'RETURNED']);
const CLOSED_ORDER_STATUSES = new Set(['OUTBOUNDED', 'CLOSED', 'RESOLVED', 'MERGED', 'RETURNED', 'RETURNED_PARTIAL']);

/** The carrier's unified status, read into the desk's vocabulary (rule 15). */
const CARRIER_STATE: Readonly<Record<string, ParcelState>> = {
  DELIVERED: 'DELIVERED',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  ORDER_PLACED: 'IN_TRANSIT',
  PICKED_UP: 'IN_TRANSIT',
  IN_TRANSIT: 'IN_TRANSIT',
  CUSTOMS_CLEARANCE: 'IN_TRANSIT',
  ARRIVED_AT_DESTINATION: 'IN_TRANSIT',
  HANDED_TO_LOCAL_CARRIER: 'IN_TRANSIT',
  LOCAL_IN_TRANSIT: 'IN_TRANSIT',
  FAILED_ATTEMPT: 'PROBLEM',
  HELD_FOR_PICKUP: 'PROBLEM',
  CUSTOMS_HOLD: 'PROBLEM',
  EXCEPTION: 'PROBLEM',
  RETURNING: 'PROBLEM',
  RETURNED: 'PROBLEM',
  LOST: 'PROBLEM',
  CANCELLED: 'PROBLEM',
  EXPIRED: 'PROBLEM',
};

/** One parcel the desk scanned today, as this browser remembers it (rules 11, 13, 14). */
export interface TodayScan {
  code: string;
  /** When the code was first scanned today, ISO. */
  firstAt: string;
  /** When it was last judged, ISO. */
  lastAt: string;
  cls: SortClass;
  /** The rebin group the parcel went to, or would go to once its order completes (WAIT/HOLD). */
  group: MultiGroup | null;
  orderIds: number[];
  /** Units of goods the parcel carries, for the group tie-break (rule 10). */
  units: number;
  /** Set once a later scan told the desk to take this parcel out of Wait/Hold. */
  pulledAt?: string | null;
  pulledInto?: SortClass | null;
}

export interface LineVerdict {
  orderId: number;
  lineNo: number;
  sku: string;
  name: string;
  qty: number;
  route: string;
  /** Picked from the shelf rather than bought for this order (rule 10). */
  isStock: boolean;
  numbers: string[];
  state: ParcelState;
  /** The carrier's own word, when one is on the tracking row. */
  carrierStatus: string | null;
}

export interface OrderVerdict {
  orderId: number;
  status: string;
  placedAt: string | null;
  ageDays: number | null;
  urgent: boolean;
  holdReason: string | null;
  temporaryLocation: string | null;
  cancelled: boolean;
  closed: boolean;
  lines: LineVerdict[];
  /** Active lines already inbounded, over all active lines (the sheet's "#주문 2/3"). */
  inCount: number;
  activeCount: number;
}

export interface Relocation {
  code: string;
  cls: SortClass;
  firstAt: string;
}

export interface SortVerdict {
  code: string;
  cls: SortClass;
  /** For WAIT and HOLD: the group the bundle goes to once complete; for MULTI the group itself. */
  group: MultiGroup | null;
  rule: number;
  /** Short reasons, each already readable on its own. */
  reasons: string[];
  orders: OrderVerdict[];
  /** Lines this parcel carries. */
  contents: LineVerdict[];
  /** Lines of the bundle not yet here, with how each stands. */
  waitingOn: LineVerdict[];
  urgent: boolean;
  includesCancelled: boolean;
  /** Parcels from today's log the desk should now move (rule 11). */
  relocate: Relocation[];
  /** Parcels from today's log already sitting where this one goes, to gather beside it. */
  gatherWith: Relocation[];
  /** When this code was first scanned today, if it was (rule 13). */
  rescanOf: string | null;
  units: number;
  /** The lookup stopped short of the whole bundle; the verdict says so rather than pretending. */
  truncated: boolean;
}

const KST_DAY = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** The KST calendar day of an instant, `YYYY-MM-DD`; null on anything that is not a time. */
export const kstDayOf = (iso: string | null | undefined): string | null => {
  if (!iso) {
    return null;
  }
  const at = new Date(/(Z|[+-]\d{2}:?\d{2})$/.test(iso) ? iso : `${iso}Z`);
  return Number.isNaN(at.getTime()) ? null : KST_DAY.format(at);
};

/** Whole KST calendar days between two instants (`to` minus `from`). */
export const kstDaysBetween = (fromIso: string | null | undefined, toIso: string): number | null => {
  const from = kstDayOf(fromIso);
  const to = kstDayOf(toIso);
  if (!from || !to) {
    return null;
  }
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
};

const URGENT_DAYS = 3;

const nameOf = (line: FulfillmentOrderLine): string =>
  [line.brand, line.productName ?? line.productNameKr ?? line.originalProductName ?? line.sku]
    .filter(Boolean)
    .join(' ');

/** Every parcel number a line is known by: the number on the line, and every inbound tracking row of it. */
export const parcelNumbersOf = (detail: FulfillmentOrderDetail, line: FulfillmentOrderLine): string[] => {
  const ref = `${detail.header.orderId}#${line.lineNo}`;
  const numbers = new Set<string>();
  const own = line.lineTrackingNumber?.trim();
  if (own) {
    numbers.add(own);
  }
  for (const row of detail.tracking ?? []) {
    if (row.kind === 'INBOUND' && row.refId === ref && row.number?.trim()) {
      numbers.add(row.number.trim());
    }
  }
  return [...numbers];
};

interface CarrierReading {
  status: string | null;
  /** The KST day of the latest DELIVERED event, when the row carries one. */
  deliveredDay: string | null;
}

const carrierStatusOf = (detail: FulfillmentOrderDetail, numbers: string[]): CarrierReading => {
  for (const row of detail.tracking ?? []) {
    if (row.kind === 'INBOUND' && numbers.includes(row.number?.trim()) && row.unifiedStatus) {
      const delivered = (row.events ?? [])
        .filter((event) => event.event === 'DELIVERED' && event.timestamp)
        .map((event) => event.timestamp)
        .sort()
        .pop();
      return { status: row.unifiedStatus, deliveredDay: kstDayOf(delivered) };
    }
  }
  return { status: null, deliveredDay: null };
};

const sameCode = (a: string, b: string): boolean => a.trim().toUpperCase() === b.trim().toUpperCase();

/** How one line stands (rule 6), given the code in hand and the codes scanned earlier today. */
export const lineStateOf = (
  detail: FulfillmentOrderDetail,
  line: FulfillmentOrderLine,
  code: string,
  scannedToday: ReadonlySet<string>,
  today: string | null = null,
): Omit<LineVerdict, 'orderId' | 'lineNo' | 'sku' | 'name' | 'qty' | 'route'> => {
  const route = line.sourcingRoute ?? 'JIT';
  const isStock = route !== 'JIT';
  const numbers = parcelNumbersOf(detail, line);
  const carrier = carrierStatusOf(detail, numbers);
  const carrierStatus = carrier.status;
  const base = { isStock, numbers, carrierStatus };
  if (DROPPED_LINE_STATUSES.has(line.lineStatus)) {
    return { ...base, state: 'CANCELLED' };
  }
  if (IN_LINE_STATUSES.has(line.lineStatus)) {
    return { ...base, state: 'IN' };
  }
  if (numbers.some((number) => sameCode(number, code))) {
    return { ...base, state: 'HERE' };
  }
  if (isStock) {
    return { ...base, state: 'NO_STOCK' };
  }
  if (numbers.length === 0) {
    return { ...base, state: 'NO_TRACKING' };
  }
  if (numbers.some((number) => scannedToday.has(number.trim().toUpperCase()))) {
    return { ...base, state: 'SCANNED_TODAY' };
  }
  const state = carrierStatus ? (CARRIER_STATE[carrierStatus] ?? 'UNKNOWN') : 'UNKNOWN';
  // A parcel the carrier left on an earlier day and nobody scanned since is not "here" — it is missing in the building.
  if (state === 'DELIVERED') {
    // A delivery is only "here" when it landed today; one left on an earlier day and never scanned is missing
    // in the building, and one without a date cannot be placed on any day at all.
    if (!carrier.deliveredDay || !today) {
      return { ...base, state: 'UNKNOWN' };
    }
    if (carrier.deliveredDay < today) {
      return { ...base, state: 'DELIVERED_EARLIER' };
    }
  }
  return { ...base, state };
};

/** One order read into the desk's terms. */
export const orderVerdictOf = (
  detail: FulfillmentOrderDetail,
  code: string,
  scannedToday: ReadonlySet<string>,
  nowIso: string,
): OrderVerdict => {
  const header = detail.header;
  const lines: LineVerdict[] = detail.lines.map((line) => ({
    orderId: header.orderId,
    lineNo: line.lineNo,
    sku: line.sku,
    name: nameOf(line),
    qty: line.qtyOrdered,
    route: line.sourcingRoute ?? 'JIT',
    ...lineStateOf(detail, line, code, scannedToday, kstDayOf(nowIso)),
  }));
  const active = lines.filter((line) => line.state !== 'CANCELLED');
  const ageDays = kstDaysBetween(header.orderPlacedAt, nowIso);
  const cancelled = header.fulfillmentStatus === 'CANCELLED' || (lines.length > 0 && active.length === 0);
  return {
    orderId: header.orderId,
    status: header.fulfillmentStatus,
    placedAt: header.orderPlacedAt,
    ageDays,
    urgent: ageDays != null && ageDays >= URGENT_DAYS,
    holdReason: header.holdReason?.trim() ? header.holdReason.trim() : null,
    temporaryLocation: header.temporaryLocation?.trim() ? header.temporaryLocation.trim() : null,
    cancelled,
    closed: !cancelled && CLOSED_ORDER_STATUSES.has(header.fulfillmentStatus),
    lines,
    inCount: active.filter((line) => line.state === 'IN').length,
    activeCount: active.length,
  };
};

const multiGroupCounts = (log: readonly TodayScan[], group: MultiGroup) => {
  const rows = log.filter((scan) => scan.group === group && (scan.cls === group || scan.pulledInto === group));
  return { parcels: rows.length, units: rows.reduce((sum, scan) => sum + scan.units, 0) };
};

/**
 * The rebin group a bundle goes to (rule 10): Multi 1 without shelf picking; with picking, the group an
 * order of the bundle already went to today, else whichever of 2 and 3 took fewer parcels today, then
 * fewer units, then 2.
 */
export const pickMultiGroup = (
  hasStock: boolean,
  orderIds: readonly number[],
  log: readonly TodayScan[],
): MultiGroup => {
  if (!hasStock) {
    return 'MULTI_1';
  }
  const sticky = log.find(
    (scan) =>
      (scan.group === 'MULTI_2' || scan.group === 'MULTI_3') && scan.orderIds.some((id) => orderIds.includes(id)),
  );
  if (sticky?.group) {
    return sticky.group;
  }
  const two = multiGroupCounts(log, 'MULTI_2');
  const three = multiGroupCounts(log, 'MULTI_3');
  if (three.parcels !== two.parcels) {
    return three.parcels < two.parcels ? 'MULTI_3' : 'MULTI_2';
  }
  return three.units < two.units ? 'MULTI_3' : 'MULTI_2';
};

const describeLine = (line: LineVerdict): string => {
  const parcel = line.numbers.length > 0 ? ` (parcel ${line.numbers.join(', ')})` : '';
  const carrier = line.carrierStatus ? ` · carrier ${line.carrierStatus}` : '';
  return `#${line.orderId} ${line.name} ×${line.qty}: ${PARCEL_STATE_LABEL[line.state]}${parcel}${carrier}`;
};

export interface JudgeInput {
  code: string;
  details: FulfillmentOrderDetail[];
  log: readonly TodayScan[];
  nowIso: string;
  truncated?: boolean;
  /** Codes scanned at this desk whose own lookup has not finished yet; they count as here (rule 6 ③). */
  alsoScanned?: readonly string[];
}

/** The whole judgement for one scanned parcel: rules 2–13 in the order the sheet's flow reads them. */
export const judgeParcel = ({
  code,
  details,
  log,
  nowIso,
  truncated = false,
  alsoScanned = [],
}: JudgeInput): SortVerdict => {
  const key = code.trim().toUpperCase();
  const scannedToday = new Set(
    [...log.map((scan) => scan.code), ...alsoScanned]
      .map((scanned) => scanned.trim().toUpperCase())
      .filter((scanned) => scanned !== key),
  );
  const orders = details.map((detail) => orderVerdictOf(detail, code, scannedToday, nowIso));
  const orderIds = orders.map((order) => order.orderId);
  const allLines = orders.flatMap((order) => order.lines);
  const contents = allLines.filter((line) => line.numbers.some((number) => sameCode(number, code)));
  const liveOrders = orders.filter((order) => !order.cancelled);
  const liveLines = liveOrders.flatMap((order) => order.lines).filter((line) => line.state !== 'CANCELLED');
  const includesCancelled =
    orders.some((order) => order.cancelled) || allLines.some((line) => line.state === 'CANCELLED');
  const urgent = liveOrders.some((order) => order.urgent);
  const units = contents.filter((line) => line.state !== 'CANCELLED').reduce((sum, line) => sum + line.qty, 0);
  const hasStock = liveLines.some((line) => line.isStock);
  const earlier = log.find((scan) => scan.code.toUpperCase() === key) ?? null;
  const reasons: string[] = [];

  if (earlier) {
    reasons.push(
      `Already scanned today at ${kstClock(earlier.firstAt)}. Judged again on current data (rule ${RULE.RESCAN})`,
    );
  }

  const bundleLog = log.filter(
    (scan) => scan.code.toUpperCase() !== key && scan.orderIds.some((id) => orderIds.includes(id)),
  );
  const parked = bundleLog.filter((scan) => (scan.cls === 'WAIT' || scan.cls === 'HOLD') && !scan.pulledAt);

  const verdict = (cls: SortClass, rule: number, group: MultiGroup | null, waitingOn: LineVerdict[]): SortVerdict => {
    const packed = cls === 'SINGLE' || cls === 'MULTI_1' || cls === 'MULTI_2' || cls === 'MULTI_3';
    const toRelocation = (scan: TodayScan): Relocation => ({ code: scan.code, cls: scan.cls, firstAt: scan.firstAt });
    return {
      code,
      cls,
      group,
      rule,
      reasons,
      orders,
      contents,
      waitingOn,
      urgent,
      includesCancelled,
      relocate: packed ? parked.map(toRelocation) : [],
      gatherWith: cls === 'WAIT' ? parked.filter((scan) => scan.cls === 'WAIT').map(toRelocation) : [],
      rescanOf: earlier?.firstAt ?? null,
      units,
      truncated,
    };
  };

  // Rule 2: a bundle read only in part is not sorted — a verdict on half a bundle can split it.
  if (truncated) {
    reasons.unshift(
      `Could not read the whole bundle (too many linked orders or parcels, or a failed lookup). Check it on the order screen (rule ${RULE.BUNDLE})`,
    );
    return verdict('CHECK', RULE.BUNDLE, null, []);
  }

  // Rule 3: a bundle that is all cancelled is not packed at all.
  if (orders.length > 0 && liveOrders.length === 0) {
    reasons.unshift(
      `Every order of the bundle is cancelled: ${orderIds.map((id) => `#${id}`).join(', ')} (rule ${RULE.CANCELLED})`,
    );
    return verdict('CANCELLED', RULE.CANCELLED, null, []);
  }
  if (includesCancelled) {
    reasons.push(`Cancelled orders or items are mixed in; judged without them (rule ${RULE.CANCELLED})`);
  }

  // Rule 4: things the desk cannot sort without a person looking.
  const closed = liveOrders.filter((order) => order.closed);
  if (closed.length > 0) {
    reasons.unshift(
      `${closed.map((order) => `#${order.orderId}(${order.status})`).join(', ')}: already outbounded or closed (rule ${RULE.CHECK})`,
    );
    return verdict('CHECK', RULE.CHECK, null, []);
  }
  // Only goods of a live order count as this parcel's contents: a cancelled order's line keeps its status.
  const liveOrderIds = new Set(liveOrders.map((order) => order.orderId));
  const liveContents = contents.filter((line) => line.state !== 'CANCELLED' && liveOrderIds.has(line.orderId));
  if (liveContents.length === 0) {
    if (contents.length > 0) {
      reasons.unshift(
        `Every item in this parcel belongs to a cancelled order or line. Do not combine it with the other orders of the bundle (rule ${RULE.CANCELLED})`,
      );
      return verdict('CANCELLED', RULE.CANCELLED, null, []);
    }
    reasons.unshift(`The parcel is linked to an order but to none of its lines (rule ${RULE.CHECK})`);
    return verdict('CHECK', RULE.CHECK, null, []);
  }
  if (liveContents.some((line) => line.state === 'IN')) {
    const shelves = liveOrders.map((order) => order.temporaryLocation).filter(Boolean);
    reasons.unshift(
      `This parcel's items are already inbounded${shelves.length > 0 ? `, order shelf ${shelves.join(', ')}` : ''} (rule ${RULE.CHECK})`,
    );
    return verdict('CHECK', RULE.CHECK, null, []);
  }

  const remaining = liveLines.filter((line) => line.state !== 'IN' && line.state !== 'HERE');
  const group = pickMultiGroup(hasStock, orderIds, log);

  // Rule 5: hold before anything else is decided.
  const held = liveOrders.filter((order) => order.holdReason);
  const holdLines = liveLines.filter((line) => HOLD_STATES.has(line.state));
  if (held.length > 0 || holdLines.length > 0) {
    for (const order of held) {
      reasons.unshift(`#${order.orderId} on hold: ${order.holdReason} (rule ${RULE.HOLD})`);
    }
    for (const line of holdLines) {
      reasons.push(`${describeLine(line)} (rule ${RULE.HOLD})`);
    }
    return verdict('HOLD', RULE.HOLD, group, remaining);
  }

  // Rule 7: complete now.
  if (remaining.every((line) => ARRIVED_STATES.has(line.state))) {
    const single = !hasStock && liveLines.every((line) => line.state === 'HERE');
    if (single) {
      reasons.unshift(`Every item of the bundle is in this one parcel (rule ${RULE.COMPLETE})`);
      if (liveOrders.length > 1) {
        reasons.push(
          `${liveOrders.length} orders share this one parcel. No rebin: open the box at the packing desk and pack each order separately (rule ${RULE.BUNDLE})`,
        );
      }
      return verdict('SINGLE', RULE.COMPLETE, null, []);
    }
    reasons.unshift(
      remaining.length === 0
        ? `The other items are already inbounded. Pack them together (rule ${RULE.COMPLETE})`
        : `The other items are here today too (rule ${RULE.COMPLETE})`,
    );
    for (const line of remaining) {
      reasons.push(describeLine(line));
    }
    if (hasStock) {
      reasons.push(`Has stock-pick items, so it goes to Multi 2 or Multi 3 (rule ${RULE.GROUP})`);
    }
    return verdict(group, RULE.COMPLETE, group, []);
  }

  // Rule 8: only out-for-delivery parcels left.
  const notYet = remaining.filter((line) => !ARRIVED_STATES.has(line.state));
  if (notYet.every((line) => line.state === 'OUT_FOR_DELIVERY')) {
    reasons.unshift(
      `Every remaining parcel is out for delivery, so it should join today (rule ${RULE.OUT_FOR_DELIVERY})`,
    );
    for (const line of notYet) {
      reasons.push(`If it misses today, to Wait: ${describeLine(line)}`);
    }
    return verdict(group, RULE.OUT_FOR_DELIVERY, group, notYet);
  }

  // Rule 9: wait.
  reasons.unshift(`Some items have no evidence they arrive today (rule ${RULE.WAIT})`);
  for (const line of notYet) {
    reasons.push(describeLine(line));
  }
  return verdict('WAIT', RULE.WAIT, group, notYet);
};

/** Today's log after one more verdict: the code's own row updated, and the parcels it pulled marked. */
export const recordVerdict = (log: readonly TodayScan[], verdict: SortVerdict, nowIso: string): TodayScan[] => {
  const key = verdict.code.trim().toUpperCase();
  const pulled = new Set(verdict.relocate.map((row) => row.code.toUpperCase()));
  const next = log.map((scan) =>
    pulled.has(scan.code.toUpperCase()) ? { ...scan, pulledAt: nowIso, pulledInto: verdict.cls } : scan,
  );
  const row: TodayScan = {
    code: verdict.code.trim(),
    firstAt: verdict.rescanOf ?? nowIso,
    lastAt: nowIso,
    cls: verdict.cls,
    group: verdict.group,
    orderIds: verdict.orders.map((order) => order.orderId),
    units: verdict.units,
    pulledAt: null,
    pulledInto: null,
  };
  const at = next.findIndex((scan) => scan.code.toUpperCase() === key);
  if (at >= 0) {
    next[at] = row;
    return next;
  }
  return [...next, row];
};

/** Today's tallies by the class each parcel stands in now; a parcel pulled out of Wait counts where it went. */
export const tallyOf = (log: readonly TodayScan[]): Record<SortClass, number> & { total: number } => {
  const counts: Record<SortClass, number> = {
    SINGLE: 0,
    MULTI_1: 0,
    MULTI_2: 0,
    MULTI_3: 0,
    WAIT: 0,
    HOLD: 0,
    CANCELLED: 0,
    CHECK: 0,
  };
  for (const scan of log) {
    counts[scan.pulledInto ?? scan.cls] += 1;
  }
  return { ...counts, total: log.length };
};

/** Whether two verdicts send the parcel to the same place with the same prompts, so a re-check can stay quiet. */
export const sameVerdict = (a: SortVerdict, b: SortVerdict): boolean => {
  const shape = (verdict: SortVerdict) =>
    JSON.stringify({
      cls: verdict.cls,
      group: verdict.group,
      relocate: verdict.relocate.map((row) => row.code).sort(),
      gather: verdict.gatherWith.map((row) => row.code).sort(),
      waiting: verdict.waitingOn.map((line) => `${line.orderId}-${line.lineNo}:${line.state}`).sort(),
      orders: verdict.orders.map((order) => order.orderId).sort(),
    });
  return shape(a) === shape(b);
};

/**
 * Today's log with one verdict taken back: the parcels it pulled out of Wait go back, and the code's own row
 * returns to what it was before (or leaves, if it was not there). Used when the server re-check disagrees
 * with the verdict the desk showed from memory (rule 16).
 */
export const undoVerdict = (
  log: readonly TodayScan[],
  verdict: SortVerdict,
  at: string,
  previousRow: TodayScan | null,
): TodayScan[] => {
  const key = verdict.code.trim().toUpperCase();
  const pulled = new Set(verdict.relocate.map((row) => row.code.toUpperCase()));
  const restored = log.map((scan) =>
    pulled.has(scan.code.toUpperCase()) && scan.pulledAt === at ? { ...scan, pulledAt: null, pulledInto: null } : scan,
  );
  const index = restored.findIndex((scan) => scan.code.toUpperCase() === key);
  if (index < 0) {
    return restored;
  }
  if (previousRow) {
    restored[index] = previousRow;
    return restored;
  }
  return restored.filter((_, position) => position !== index);
};
